import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import SEOResearchBlock from "../components/SEOResearchBlock";
import { newsArticles } from "../data/newsArticles";
import { xRoboticsArticles } from "../data/xRoboticsArticles";
import { seoConfig } from "../data/seoConfig";

export default function RoboticsNews() {
  const [query, setQuery] = useState("");
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics News", to: "/robotics-news" },
  ];

  const articles = useMemo(
    () =>
      Array.from(
        new Map(
          [...xRoboticsArticles, ...newsArticles]
            .filter((article) => article.path)
            .sort((a, b) =>
              String(b.datePublished || "").localeCompare(
                String(a.datePublished || ""),
              ),
            )
            .map((article) => [
              article.tweetId || article.slug || article.path,
              article,
            ]),
        ).values(),
      ),
    [],
  );

  const filteredArticles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return articles;

    return articles.filter((article) =>
      [
        article.title,
        article.description,
        article.category,
        article.primaryKeyword,
        ...(article.tags || []),
        ...(article.keywords || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [articles, query]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Robotics News",
    description:
      "Robotics news, analysis and technical guides covering humanoid robots, Physical AI, industrial automation, robot hardware and deployment evidence.",
    url: `${seoConfig.baseUrl}/robotics-news`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: articles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: article.title,
        url: `${seoConfig.baseUrl}${article.path}`,
      })),
    },
  };

  return (
    <>
      <SEO
        title="Humanoid robot, Physical AI and robotics news"
        description="Robotics news, analysis and technical guides covering humanoid robots, Physical AI, industrial automation, robot hardware, company launches and deployment evidence."
        path="/robotics-news"
        type="website"
        schema={[buildBreadcrumbSchema(breadcrumbs), articleSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Robotics news"
        title="Robotics News"
        description="Browse dated robotics reports, deployment analysis and technical guides. Each article states its own topic and publication date."
      />

      <section className="site-shell py-12 sm:py-16">
        <div className="mb-8 rounded-[1.5rem] border border-neutral-200 bg-white p-5">
          <label
            htmlFor="robotics-news-search"
            className="text-sm font-black text-neutral-950"
          >
            Search robotics news
          </label>
          <input
            id="robotics-news-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search robots, companies or technologies"
            className="mt-3 w-full rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-base text-neutral-950 outline-none transition focus:border-blue-400"
          />
          <p
            className="mt-3 text-sm text-neutral-600"
            role="status"
            aria-live="polite"
          >
            {filteredArticles.length} article
            {filteredArticles.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {filteredArticles.map((article) => (
            <article
              key={article.id}
              className="soft-card overflow-hidden p-0 transition hover:border-blue-200"
            >
              {article.image && (
                <img
                  src={article.image}
                  alt={article.imageAlt || article.title}
                  width={article.imageWidth || 1200}
                  height={article.imageHeight || 630}
                  className="aspect-[1200/630] w-full border-b border-neutral-200 object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-neutral-500">
                  <span>{article.category}</span>
                  {article.datePublished && (
                    <>
                      <span aria-hidden="true">·</span>
                      <time dateTime={article.datePublished}>
                        {new Intl.DateTimeFormat("en", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }).format(new Date(`${article.datePublished}T00:00:00Z`))}
                      </time>
                    </>
                  )}
                </div>
                <h2 className="mt-3 text-2xl font-bold tracking-tight text-neutral-950">
                  {article.title}
                </h2>
                <p className="mt-3 text-sm leading-6 text-neutral-600">
                  {article.description}
                </p>
                {article.path && (
                  <Link
                    to={article.path}
                    className="mt-5 inline-flex rounded-full bg-neutral-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700"
                  >
                    Read article
                  </Link>
                )}
              </div>
            </article>
          ))}
          {!filteredArticles.length && (
            <div className="soft-card p-8 md:col-span-2">
              <h2 className="text-2xl font-black text-neutral-950">
                No matching articles
              </h2>
              <p className="mt-3 text-neutral-600">
                Try a robot name, company, technology or application.
              </p>
            </div>
          )}
        </div>
      </section>
      <SEOResearchBlock />
      <CTASection title="Follow TechniaHQRobot for robotics news" />
    </>
  );
}
